import React, { useEffect, useState, useMemo } from "react";
import { Container } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import UserTable from "../components/UserTable";
import { useUser } from "../../../apis/user/UserProvider";
import { getPageName } from "../../../locales/translationUtils";
import { useTranslation } from 'react-i18next';
import NavBar, { NavBarItem } from "../../../components/NavBar";
import { GridColDef } from "@mui/x-data-grid";
import AlertOverlay from "../../../components/Alert";
import { FaEdit } from "react-icons/fa";

const UserView: React.FC = () => {
    const { t } = useTranslation();
    const { usuarios, apiResponse, fetchUsuarios } = useUser();
    const navigate = useNavigate();
    const location = useLocation();
    const [alert, setAlert] = useState<{ message: string; variant: "success" | "danger" | "warning" | "info" } | null>(null);
    const [searchTerm, setSearchTerm] = useState<string>("");

    const [paginationModel, setPaginationModel] = React.useState({
        pageSize: 20,
        page: 0,
    });

    // Buscar todos os usuários de uma vez (client-side)
    useEffect(() => {
        fetchUsuarios();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Filtrar usuários localmente pelo searchTerm
    const filteredUsers = useMemo(() => {
        if (!usuarios) return [];
        if (!searchTerm.trim()) return usuarios;
        
        const term = searchTerm.toLowerCase();
        return usuarios.filter(u => 
            (u.username?.toLowerCase().includes(term)) ||
            (u.fullName?.toLowerCase().includes(term)) ||
            (u.email?.toLowerCase().includes(term))
        );
    }, [usuarios, searchTerm]);

    useEffect(() => {
        if (location.state?.message) {
            setAlert({ message: location.state.message, variant: location.state.variant });

            navigate(location.pathname, { replace: true });

            const timeout = setTimeout(() => setAlert(null), 5000);
            return () => clearTimeout(timeout);
        }
    }, [location, navigate]);

    const navItems: NavBarItem[] = [
        { label: t('user.labels.home'), route: "/", iconName: "house-door" },
        { label: t('user.labels.new'), route: "/User/create", iconName: "plus-circle" },
    ];

    const columns: GridColDef[] = [
        {
            field: "id",
            headerName: t('user.fields.id'),
            width: 90,
            sortable: true,
            filterable: true,
            renderCell: (params) => (
                <span style={{
                    display: "inline-flex",
                    alignItems: "center",
                    color: "black",
                    cursor: "pointer"
                }}
                    onClick={() => navigate(`/user/${params.row.id}`)}>
                        <FaEdit className="text-primary" style={{ marginRight: "6px", marginLeft: "2px" }}/>
                        {params.row.id}
                </span>
            ),
        },
        {
            field: "fullName",
            headerName: t('user.fields.name'),
            width: 300,
            sortable: true,
            filterable: true,
            renderCell: (params) => params.row?.fullName || ''
        },
        {
            field: "username",
            headerName: t('user.fields.username'),
            width: 200,
            sortable: true,
            filterable: true,
            renderCell: (params) => params.row?.username || ''
        },
        {
            field: "email",
            headerName: t('user.fields.email'),
            width: 250,
            sortable: true,
            filterable: true,
            renderCell: (params) => params.row?.email || '-'
        },
        {
            field: "role",
            headerName: t('user.fields.role'),
            width: 200,
            sortable: true,
            filterable: true,
            valueGetter: (value: any, row: any) => {
                const roles = row?.secUserSecRoles?.map((r: any) => r.secRole?.authority).filter(Boolean).join(', ');
                return roles || '';
            },
            renderCell: (params) => {
                const roles = params.row?.secUserSecRoles?.map((r: any) => r.secRole?.authority).filter(Boolean).join(', ');
                return roles || '-';
            }
        },
        {
            field: "accountExpired",
            headerName: t('user.fields.accountExpired'),
            width: 150,
            sortable: true,
            filterable: true,
            type: 'boolean',
            renderCell: (params) => String(params.row?.accountExpired)
        },
        {
            field: "accountLocked",
            headerName: t('user.fields.accountLocked'),
            width: 150,
            sortable: true,
            filterable: true,
            type: 'boolean',
            renderCell: (params) => String(params.row?.accountLocked)
        },
        {
            field: "enabled",
            headerName: t('user.fields.active'),
            width: 150,
            sortable: true,
            filterable: true,
            type: 'boolean',
            renderCell: (params) => String(params.row?.enabled)
        },
    ];

    return (
        <Container fluid style={{ height: 'calc(100vh - 200px)', overflow: 'auto' }}>
            <NavBar items={navItems} />
            <div className="mb-3"></div>
            <h2>{getPageName("user")}</h2>
            <div className="mb-3"></div>
            
            {/* Campo de busca */}
            <div className="mb-3">
                <input
                    type="text"
                    className="form-control"
                    placeholder={t('user.labels.search') || "Buscar usuário..."}
                    value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        // Reset para primeira página quando buscar
                        setPaginationModel({ ...paginationModel, page: 0 });
                    }}
                    style={{ maxWidth: '400px' }}
                />
            </div>
            
            <div>
                {alert && (
                    <AlertOverlay
                        message={alert.message}
                        variant={alert.variant}
                        onClose={() => setAlert(null)}
                    />
                )}
            </div>
            <UserTable
                rows={filteredUsers}
                columns={columns}
                getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? "even-row" : "odd-row")}
                apiResponse={apiResponse} 
                paginationModel={paginationModel}
                setPaginationModel={setPaginationModel}
            />
        </Container>
    );
};

export default UserView;